import { Link } from "react-router-dom";
import {
  Lightning, Terminal, Package, ShieldCheck, ArrowRight, GithubLogo, Stack, Printer,
  UserCircleGear, SlidersHorizontal, Snowflake, ClockCounterClockwise,
  Sparkle, CheckCircle, Cpu,
} from "@phosphor-icons/react";
import { WordsPullUpMultiStyle } from "../components/ui/words-pull-up";

const providers = [
  { name: "Jamf Pro", color: "#0A84FF" },
  { name: "Kandji", color: "#7C5CFC" },
  { name: "Mosyle", color: "#00C2A8" },
  { name: "Intune", color: "#0078D4" },
  { name: "Addigy", color: "#FF6A3D" },
  { name: "Iru", color: "#F5C518" },
];

const stats = [
  { value: "6", label: "MDM providers" },
  { value: "19+", label: "Plug & play tools" },
  { value: "9", label: "Zero-token templates" },
  { value: "0", label: "LLM tokens on visual builders" },
];

const features = [
  { icon: Terminal, title: "AI Script Builder", tag: "AI", desc: "Describe intent in plain English — get idempotent bash scripts tuned to your MDM's deployment model, with streaming output." },
  { icon: ShieldCheck, title: "Config Profiles", tag: "AI", desc: "Generate valid .mobileconfig payloads for restrictions, security and settings — validated before download." },
  { icon: SlidersHorizontal, title: "Deploy Software", tag: "0-token", desc: "A visual editor for all 19 catalog tools — build a config profile and deploy script for each, no scripting." },
  { icon: Snowflake, title: "MenuBar Forge", tag: "0-token", desc: "Design the macOS menu bar with a live preview — item spacing, reveal behavior and one-shot install." },
  { icon: Printer, title: "Printer Automation", tag: "0-token", desc: "Build lpadmin install scripts for any protocol with driver handling — single printers or a whole print server." },
  { icon: UserCircleGear, title: "SSO / Login Builder", tag: "0-token", desc: "XCreds, Platform SSO and Login-Window configurators with IdP presets, setup guides and a live login preview." },
  { icon: Stack, title: "Zero-Token Templates", tag: "0-token", desc: "Deterministic builders for Rosetta, Dock, FileVault, Wi-Fi, wallpaper, admin accounts and more — instant, free." },
  { icon: Package, title: "Software Catalog", tag: "Curated", desc: "19 curated open-source MacAdmins tools with category, search and per-MDM compatibility filters." },
  { icon: ClockCounterClockwise, title: "Projects & History", tag: "Workspace", desc: "Save every artifact, keep version history, roll back to any point, and export multi-file zip bundles." },
];

const showcase = [
  { img: "/screenshots/menubar.jpeg", title: "MenuBar Forge", desc: "Design the menu bar with a live preview — spacing, behavior & install." },
  { img: "/screenshots/deploy.jpeg", title: "Deploy Software", desc: "A schema-driven visual editor for all 19 catalog tools." },
  { img: "/screenshots/sso.jpeg", title: "SSO / Login Builder", desc: "XCreds & Platform SSO with step-by-step IdP setup guides." },
  { img: "/screenshots/generator.jpeg", title: "AI Generator", desc: "Plain-English intent → deploy-ready scripts & profiles." },
];

const catalog = [
  { name: "Support App", cat: "End User" },
  { name: "Escrow Buddy", cat: "Security" },
  { name: "Nudge", cat: "Patch Mgmt" },
  { name: "Installomator", cat: "App Deploy" },
  { name: "swiftDialog", cat: "End User" },
  { name: "Munki", cat: "App Deploy" },
  { name: "DEPNotify", cat: "Onboarding" },
  { name: "Rosetta 2", cat: "Compatibility" },
  { name: "Privileges", cat: "Security" },
  { name: "S.U.P.E.R.MAN", cat: "Patch Mgmt" },
  { name: "DockUtil", cat: "End User" },
  { name: "Outset", cat: "Automation" },
  { name: "AutoPkg", cat: "App Deploy" },
  { name: "MacAdmins Python", cat: "Compatibility" },
  { name: "erase-install", cat: "Patch Mgmt" },
  { name: "mist-cli", cat: "App Deploy" },
  { name: "desktoppr", cat: "End User" },
  { name: "Baseline", cat: "Onboarding" },
  { name: "Setup Your Mac", cat: "Onboarding" },
];

const steps = [
  { n: "01", title: "Configure or describe", desc: "Pick a visual builder and fill the form, or just describe what you need to the AI generator." },
  { n: "02", title: "Preview live", desc: "See a live menu-bar, login-screen or app preview and the exact profile / script before you ship it." },
  { n: "03", title: "Deploy & version", desc: "Download, bundle as a zip, or save to your workspace with full version history and rollback." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0A0A0B] grain text-[#FAFAFA]">
      <style>{`
        @keyframes riseIn { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: none; } }
        .rise { opacity: 0; animation: riseIn 0.7s cubic-bezier(0.22,1,0.36,1) forwards; }
      `}</style>

      <header className="border-b border-[#27272A] relative z-[3] sticky top-0 bg-[#0A0A0B]/85 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 border border-[#00F0FF] flex items-center justify-center">
              <Lightning size={18} weight="fill" className="text-[#00F0FF]" />
            </div>
            <span className="font-semibold tracking-tight">MDM Forge</span>
          </div>
          <div className="flex items-center gap-1 sm:gap-3">
            <a href="#features" data-testid="header-features" className="hidden sm:block text-sm text-[#A1A1AA] hover:text-[#FAFAFA] transition-colors duration-200 px-3 py-2">Features</a>
            <Link to="/how-it-works" data-testid="header-how" className="hidden sm:block text-sm text-[#A1A1AA] hover:text-[#FAFAFA] transition-colors duration-200 px-3 py-2">How it works</Link>
            <Link to="/login" data-testid="header-login" className="text-sm text-[#A1A1AA] hover:text-[#FAFAFA] transition-colors duration-200 px-3 py-2">Sign in</Link>
            <Link to="/register" data-testid="header-register" className="text-sm bg-[#00F0FF] text-[#0A0A0B] font-medium px-4 py-2 hover:bg-[#00d4e0] transition-colors duration-200">Get started</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#27272A]">
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1718844054447-42759fd577e5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600')", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-[2] max-w-6xl mx-auto px-6 py-24 lg:py-28 grid lg:grid-cols-[1.1fr_0.9fr] gap-14 items-center">
          <div>
            <div className="rise inline-flex items-center gap-2 border border-[#27272A] bg-[#121214] px-3 py-1.5 mb-8 font-mono text-xs text-[#00F0FF]" style={{ animationDelay: "0ms" }}>
              <span className="w-1.5 h-1.5 bg-[#00FF41] rounded-full" /> AI-POWERED · APPLE MDM · 0-TOKEN BUILDERS
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light max-w-3xl leading-[1.05]">
              <WordsPullUpMultiStyle
                segments={[
                  { text: "Build plug & play software," },
                  { text: "scripts & profiles", className: "text-[#00F0FF]" },
                  { text: "for any Apple MDM." },
                ]}
              />
            </h1>
            <p className="rise text-base text-[#A1A1AA] mt-6 max-w-xl font-mono leading-relaxed" style={{ animationDelay: "160ms" }}>
              An entire Mac admin toolkit in one place — AI generation plus zero-token visual builders for
              software, menu bars, printers, SSO and configuration profiles.
            </p>
            <div className="rise flex flex-wrap items-center gap-4 mt-10" style={{ animationDelay: "240ms" }}>
              <Link to="/register" data-testid="hero-cta" className="inline-flex items-center gap-2 bg-[#00F0FF] text-[#0A0A0B] font-medium px-6 py-3 hover:bg-[#00d4e0] transition-colors duration-200">
                Start forging <ArrowRight size={18} />
              </Link>
              <a href="#features" data-testid="hero-explore" className="inline-flex items-center gap-2 border border-[#27272A] px-6 py-3 text-sm text-[#A1A1AA] hover:text-[#FAFAFA] hover:border-[#3f3f46] transition-colors duration-200">
                <Sparkle size={18} /> Explore features
              </a>
            </div>
            <div className="rise flex flex-wrap gap-2 mt-12" style={{ animationDelay: "320ms" }}>
              {providers.map((p) => (
                <span key={p.name} className="flex items-center gap-2 font-mono text-xs text-[#71717A] border border-[#27272A] px-3 py-1.5">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: p.color }} /> {p.name}
                </span>
              ))}
            </div>
          </div>

          {/* Mock terminal artifact */}
          <div className="rise hidden lg:block" style={{ animationDelay: "300ms" }} data-testid="hero-mock">
            <div className="border border-[#27272A] bg-[#050505] shadow-2xl shadow-black/50">
              <div className="h-9 bg-[#1c1c1f] border-b border-[#27272A] flex items-center px-4 gap-2">
                <span className="w-3 h-3 rounded-full bg-[#FF5F56]" />
                <span className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
                <span className="w-3 h-3 rounded-full bg-[#28C840]" />
                <span className="ml-3 font-mono text-[11px] text-[#71717A]">deploy-nudge.sh</span>
              </div>
              <pre className="p-5 font-mono text-[12px] leading-relaxed overflow-hidden">
<span className="text-[#71717A]">#!/bin/bash</span>{"\n"}
<span className="text-[#71717A]"># Generated by MDM Forge · 0 tokens</span>{"\n"}
<span className="text-[#569CD6]">set</span> -euo pipefail{"\n"}
PKG_URL=<span className="text-[#00FF41]">"$1"</span>{"\n"}
<span className="text-[#569CD6]">echo</span> <span className="text-[#00FF41]">"Installing Nudge..."</span>{"\n"}
/usr/sbin/installer -pkg <span className="text-[#00FF41]">"$PKG"</span> -target /{"\n"}
<span className="text-[#569CD6]">echo</span> <span className="text-[#00FF41]">"Done."</span> <span className="text-[#00F0FF]">▍</span>
              </pre>
            </div>
            <div className="mt-3 border border-[#27272A] bg-[#0f0f11] px-4 py-3 flex items-center gap-3">
              <ShieldCheck size={16} className="text-[#00FF41]" />
              <span className="font-mono text-[11px] text-[#A1A1AA]">Validated .mobileconfig &amp; script · ready to upload</span>
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="relative z-[2] border-t border-[#27272A] bg-[#0A0A0B]/60">
          <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 divide-x divide-[#27272A] border-x border-[#27272A]">
            {stats.map((s) => (
              <div key={s.label} className="px-6 py-6">
                <div className="text-3xl font-light text-[#00F0FF] tracking-tight">{s.value}</div>
                <div className="font-mono text-[11px] text-[#71717A] mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-6xl mx-auto px-6 py-24 relative z-[2] scroll-mt-20">
        <div className="max-w-2xl">
          <div className="font-mono text-xs text-[#00F0FF] mb-3">// EVERYTHING YOU CAN BUILD</div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-light">One toolkit for the entire Mac fleet lifecycle</h2>
          <p className="text-sm text-[#A1A1AA] font-mono mt-4 leading-relaxed">
            From onboarding to security to end-user experience — generate, preview and deploy every artifact your MDM needs.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#27272A] border border-[#27272A] mt-12" data-testid="features-grid">
          {features.map((f) => (
            <div key={f.title} data-testid={`feature-${f.title.replace(/[^a-z]+/gi, "-").toLowerCase()}`} className="group bg-[#0A0A0B] p-7 hover:bg-[#121214] transition-colors duration-200">
              <div className="flex items-start justify-between mb-5">
                <div className="w-10 h-10 border border-[#27272A] flex items-center justify-center group-hover:border-[#00F0FF] transition-colors duration-200">
                  <f.icon size={20} className="text-[#00F0FF]" />
                </div>
                <span className="font-mono text-[10px] text-[#71717A] border border-[#27272A] px-2 py-0.5">{f.tag}</span>
              </div>
              <h3 className="text-lg tracking-tight mb-2">{f.title}</h3>
              <p className="text-sm text-[#A1A1AA] leading-relaxed font-mono">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Product showcase — real screenshots */}
      <section className="border-t border-[#27272A] bg-[#0f0f11] relative z-[2]">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <div className="font-mono text-xs text-[#00F0FF] mb-3">// SEE IT IN ACTION</div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-light mb-12">Real builders, live previews, deploy-ready output</h2>
          <div className="grid md:grid-cols-2 gap-8" data-testid="showcase-grid">
            {showcase.map((s) => (
              <figure key={s.title} data-testid={`showcase-${s.title.replace(/[^a-z]+/gi, "-").toLowerCase()}`} className="group">
                <div className="border border-[#27272A] bg-[#050505] overflow-hidden group-hover:border-[#3f3f46] transition-colors duration-200">
                  <div className="h-8 bg-[#1c1c1f] border-b border-[#27272A] flex items-center px-4 gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F56]" />
                    <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                    <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
                  </div>
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={s.img} alt={s.title} loading="lazy"
                      className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500" />
                  </div>
                </div>
                <figcaption className="mt-4">
                  <h3 className="text-lg tracking-tight">{s.title}</h3>
                  <p className="text-sm text-[#A1A1AA] font-mono mt-1">{s.desc}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Two ways to build */}
      <section className="border-y border-[#27272A] bg-[#0f0f11] relative z-[2]">
        <div className="max-w-6xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-px bg-[#27272A] border border-[#27272A]">
          <div className="bg-[#0A0A0B] p-10">
            <Cpu size={24} className="text-[#00F0FF] mb-5" />
            <div className="font-mono text-xs text-[#00F0FF] mb-2">AI-POWERED</div>
            <h3 className="text-2xl tracking-tight font-light mb-3">Describe it, ship it</h3>
            <p className="text-sm text-[#A1A1AA] font-mono leading-relaxed">
              The AI generator turns plain-English intent into production-ready, idempotent bash scripts and valid
              configuration profiles — tuned to your provider's deployment model, streamed live.
            </p>
          </div>
          <div className="bg-[#0A0A0B] p-10">
            <Lightning size={24} weight="fill" className="text-[#00FF41] mb-5" />
            <div className="font-mono text-xs text-[#00FF41] mb-2">ZERO LLM TOKENS</div>
            <h3 className="text-2xl tracking-tight font-light mb-3">Visual builders, deterministic output</h3>
            <p className="text-sm text-[#A1A1AA] font-mono leading-relaxed">
              Deploy Software, MenuBar Forge, Printers, SSO and Templates generate the exact same artifact every time —
              with live previews and no token cost. Import &amp; export configs as JSON to share across your team.
            </p>
          </div>
        </div>
      </section>

      {/* Software catalog */}
      <section className="max-w-6xl mx-auto px-6 py-24 relative z-[2]">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <div className="font-mono text-xs text-[#00F0FF] mb-3">// SOFTWARE CATALOG</div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-light">19 plug &amp; play open-source tools</h2>
            <p className="text-sm text-[#A1A1AA] font-mono mt-4 max-w-xl leading-relaxed">
              Every curated MacAdmins tool ships with a one-click deploy script and a visual configurator.
            </p>
          </div>
          <Link to="/register" data-testid="catalog-cta" className="inline-flex items-center gap-2 border border-[#27272A] px-5 py-2.5 text-sm text-[#A1A1AA] hover:text-[#00F0FF] hover:border-[#00F0FF] transition-colors duration-200 whitespace-nowrap">
            Browse the catalog <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-[#27272A] border border-[#27272A]" data-testid="catalog-list">
          {catalog.map((t) => (
            <div key={t.name} className="bg-[#0A0A0B] px-4 py-3.5 flex items-center justify-between gap-2 hover:bg-[#121214] transition-colors duration-200">
              <div className="flex items-center gap-2.5 min-w-0">
                <Package size={15} className="text-[#00F0FF] shrink-0" />
                <span className="text-sm truncate">{t.name}</span>
              </div>
              <span className="font-mono text-[9px] text-[#71717A] hidden lg:block whitespace-nowrap">{t.cat}</span>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-6 py-24 relative z-[2]">
        <div className="font-mono text-xs text-[#00F0FF] mb-3">// HOW IT WORKS</div>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-light mb-12">Three steps from idea to deployment</h2>
        <div className="grid md:grid-cols-3 gap-px bg-[#27272A] border border-[#27272A]">
          {steps.map((s) => (
            <div key={s.n} className="bg-[#0A0A0B] p-8">
              <div className="font-mono text-4xl font-light text-[#27272A] mb-4">{s.n}</div>
              <h3 className="text-lg tracking-tight mb-2">{s.title}</h3>
              <p className="text-sm text-[#A1A1AA] font-mono leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-x-8 gap-y-3 mt-10">
          {["Live previews", "Version history & rollback", "Multi-file zip bundles", "Import / export JSON", "6 MDM providers"].map((t) => (
            <span key={t} className="flex items-center gap-2 font-mono text-xs text-[#A1A1AA]">
              <CheckCircle size={15} weight="fill" className="text-[#00FF41]" /> {t}
            </span>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-[#27272A] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{ background: "radial-gradient(circle at 50% 0%, #00F0FF, transparent 60%)" }} />
        <div className="relative z-[2] max-w-3xl mx-auto px-6 py-24 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-light">Forge your fleet's next deployment.</h2>
          <p className="text-sm text-[#A1A1AA] font-mono mt-5 max-w-lg mx-auto leading-relaxed">
            Free to start. Build, preview and download your first script or profile in minutes.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 mt-10">
            <Link to="/register" data-testid="cta-register" className="inline-flex items-center gap-2 bg-[#00F0FF] text-[#0A0A0B] font-medium px-7 py-3.5 hover:bg-[#00d4e0] transition-colors duration-200">
              Get started free <ArrowRight size={18} />
            </Link>
            <a href="https://github.com/macadmins" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-[#27272A] px-7 py-3.5 text-sm text-[#A1A1AA] hover:text-[#FAFAFA] hover:border-[#3f3f46] transition-colors duration-200">
              <GithubLogo size={18} /> MacAdmins tools
            </a>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#27272A] relative z-[2]">
        <div className="max-w-6xl mx-auto px-6 py-8 font-mono text-xs text-[#71717A] flex flex-col sm:flex-row items-center justify-between gap-3">
          <span>© 2026 MDM Forge</span>
          <div className="flex items-center gap-5">
            <Link to="/how-it-works" className="hover:text-[#FAFAFA] transition-colors duration-200">How it works</Link>
            <Link to="/login" className="hover:text-[#FAFAFA] transition-colors duration-200">Sign in</Link>
            <span>Built for Apple Developers</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
